import React, { ReactNode, useState } from 'react';
import styled from '@emotion/styled';
import { Plan, ThemeProps, TrackingPage, TrackingProperty, TrackingAction, TrackingPropertyMap } from '../../../types';
import { getContrastTextColor } from '../../../utils';
import { Mixpanel } from 'mixpanel-browser';
import getEventName from '../../../utils/getMixpanelEventName';
import getPlanDuration from '../../../utils/getPlanDuration';

export interface AccordionProps {
  /**
   * Some notes on how this component works.
   * It has been written to for maximum flexibility, thus is not tightly bound
   * to the data it displays. This means that one would need to use this Accordion's
   * child components: `AccordionItem`, `AccordionHeader` & `AccordionPanel`.
   *
   * All state logic lives in Accordion, and is merge into the pros of the its child
   * components via React.cloneElement. This way you will not need to care about the
   * togle logic, as it is already added to the child (please see `AddordionItem`).
   *
   * As it stands, only one panel is expanable at a time - though can be extended if need be.
   *
   * `AccordionItem` wraps the content and is resposible for expanding/collapsing.
   *
   * `AccordionHeader` is permanantly displayed to the user.
   *
   * `AccordionPanel` is collapsable/expandable depending on click of the wrapper
   * `AccordionItem` component.
   */

  /**
   * The css class for the input. Handled internally by emotion
   */
  className?: string;
  /**
   * The react child to render
   */
  children: ReactNode;
  /**
   * The change event handler
   */
  onChange: (args: any) => void;
  /**
   * The postion of the default expanded accordion item
   */
  initialExpanded?: number;
}

interface AccordionItemProps {
  /**
   * The react child to render
   */
  children: ReactNode;
  /**
   * The css class for the input. Handled internally by emotion
   */
  className?: string;
  /**
   * The data needed to send information on what is selected.
   */
  data: Plan;
  /**
   * The state object that controls the visibility of `AccordionPanel` content.
   * It comes from the `setState` function passed down via `React.cloneElement`
   * from its parent
   */
  expanded: boolean;
  /**
   * The change event handler
   */
  onChange: (args: any) => void;
  /**
   * The position of this panel
   */
  position: number;
  /**
   * Use this to customise the styling of your higher priority AccordionItem
   */
  priorityHighlight?: boolean;
  /**
   * The state setter that controls the visibility of `AccordionPanel` content.
   * It comes from the `setState` function passed down via `React.cloneElement`
   * from its parent
   */
  setExpanded: React.Dispatch<React.SetStateAction<StateProps>>;

  /**
   * Mixpanel related properties
   */
  mixpanel: Mixpanel;
  variant: string;
  language: string;
  currency: string;
}

export interface AccordionPanelProps extends AccordionItemProps { }

interface StateProps {
  [key: number]: boolean;
}

const initState = (
  children: ReactNode,
  initialExpanded: number | undefined
) => {
  const state: StateProps = {
    1: true
  };
  React.Children.forEach(children, (child: any) => {
    state[child.key] = initialExpanded
      ? child.key === `${initialExpanded}` // <- child.key is a string, thus we're converting `initialExpanded to a string for strict equality`
        ? true
        : false
      : false;
  });

  return state;
};

const AccordionPanel = ({
  className,
  children,
  expanded,
  position,
}: AccordionPanelProps) => {
  if (!expanded) return null;

  return (
    <section
      aria-labelledby={`acc-header-${position}`}
      className={className}
      id={`acc-panel-${position}`}>
      {children}
    </section>
  );
};

export const StyledAccordionPanel = styled((props: any) => (
  <AccordionPanel {...props} />
))`
  ${({ theme }: { theme: ThemeProps }) => ({
    backgroundColor: theme.colors.background.paper,
    borderBottomLeftRadius: theme.radii.accordion - 2,
    borderBottomRightRadius: theme.radii.accordion - 2,
    padding: '16px 16px 8px',
  })}
`;

export const StyledAccordionHeader = styled(
  ({ expanded, position, priorityHighlight, ...rest }: any) => (
    <div
      aria-controls={`acc-panel-${position}`}
      aria-expanded={expanded}
      id={`acc-header-${position}`}
      {...rest}
    />
  )
)`
  ${({
  priorityHighlight,
  theme,
  expanded,
}: {
  priorityHighlight: boolean;
  theme: ThemeProps;
  expanded: boolean;
}) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.common.transparent,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  outline: 'none',
  padding: '8px 16px',
  span: {
    color: expanded
      ? getContrastTextColor(theme.colors.accent)
      : theme.colors.text.primary,
  },
})}
`;

const createEventProperties = (variant: string, language: string, currency: string, data: any): TrackingPropertyMap => {

  const result: TrackingPropertyMap = {}
  result[TrackingProperty.TEXT_VARIANT] = variant;
  result[TrackingProperty.LANGUAGE] = language;
  result[TrackingProperty.CURRENCY] = currency;
  result[TrackingProperty.PLAN_DURATION_MONTHS] = getPlanDuration(data.productId);
  return result;
};

const AccordionItem = ({
  children,
  className,
  expanded,
  onChange,
  data,
  position,
  priorityHighlight,
  setExpanded,
  mixpanel,
  variant,
  language,
  currency
}: AccordionItemProps) => {
  const handleClick = (position: number) => {
    setExpanded((state: StateProps) => {
      /**
       * Returns a new immutable copy of state. This is what is responsible for
       * the 'one-open-at-a-time' toggle behaviour of the accordion.
       */
      const clearState = state;
      Object.keys(clearState).forEach((s: any) => {
        state[s] = false;
      });

      return {
        ...clearState,
        [position]: true,
      };
    });

    onChange(data);
    mixpanel.track(getEventName(TrackingPage.PLAN_SELECTOR, TrackingAction.SELECT_PLAN), createEventProperties(variant, language, currency, data));
  };

  return (
    <div className={className} onClick={() => handleClick(position)}>
      {React.Children.map(children, (child: any) => {
        return React.cloneElement(child, {
          expanded,
          position,
          priorityHighlight,
        });
      })}
    </div>
  );
};

export const StyledAccordionItem = styled((props: any) => (
  <AccordionItem {...props}></AccordionItem>
))`
  ${({
  expanded,
  priorityHighlight,
  theme,
}: {
  expanded: boolean;
  priorityHighlight: boolean;
  theme: ThemeProps;
}) => ({
  backgroundColor: expanded
    ? theme.colors.accent
    : theme.colors.background.paper,
  borderColor: expanded
    ? theme.colors.accent
    : expanded
      ? theme.colors.common.darkGrey
      : theme.colors.common.grey,
  borderRadius: theme.radii.accordion,
  borderStyle: 'solid',
  borderWidth: 2,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 20,
  '&:last-child': {
    marginBottom: 0,
  },
})}
`;

const Accordion = ({
  className,
  children,
  onChange,
  initialExpanded,
}: AccordionProps) => {
  const [expanded, setExpanded] = useState(() =>
    initState(children!, initialExpanded)
  );

  return (
    <div className={className}>
      {React.Children.map(children, (child: any, index: number) => {
        return React.cloneElement(child, {
          expanded: expanded[index + 1],
          onChange,
          position: index + 1,
          setExpanded,
        });
      })}
    </div>
  );
};

const StyledAccordion = styled((props: any) => <Accordion {...props} />)`
  width: 100%;
`;

export default StyledAccordion;
